import { useGetMe } from '@sara/api-client';
import { Avatar, AvatarFallback } from '@sara/ui/components/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@sara/ui/components/dropdown-menu';
import { Skeleton } from '@sara/ui/components/skeleton';
import { cn } from '@sara/ui/lib/utils';
import { useNavigate } from '@tanstack/react-router';
import { ChevronsUpDown } from 'lucide-react';
import Session from 'supertokens-auth-react/recipe/session';
import { writeLastOrgSlug } from '../../../lib/last-org';
import { useActiveOrganization } from '../hooks/use-active-organization';
import { interviewsHomeSearch } from '../../../router/tree';

export function MeDropdown() {
  const { data: me, isLoading, isError } = useGetMe();
  const { organization: activeOrg } = useActiveOrganization();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Skeleton
        data-testid="me-loading"
        className="h-9 w-full rounded-md group-data-[collapsible=icon]:size-8"
      />
    );
  }

  if (isError || !me) {
    return null;
  }

  const fullName = `${me.firstName} ${me.lastName}`;
  const initials = `${me.firstName[0] ?? ''}${me.lastName[0] ?? ''}`.toUpperCase();
  const triggerLabel = activeOrg?.name ?? fullName;
  const a11yLabel = activeOrg ? `${fullName}, ${activeOrg.name}` : fullName;

  async function handleSignOut() {
    try {
      await Session.signOut();
      await navigate({ to: '/auth' });
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  }

  function handleSwitch(slug: string) {
    if (slug === activeOrg?.slug) return;
    writeLastOrgSlug(slug);
    void navigate({
      to: '/orgs/$orgSlug/sessions',
      params: { orgSlug: slug },
      search: interviewsHomeSearch,
    });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={a11yLabel}
          className={cn(
            'group/me-trigger relative flex w-full items-center gap-2 rounded-md p-1.5 text-left text-sm',
            'transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground',
            'group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:justify-center'
          )}
        >
          <Avatar className="size-6 shrink-0">
            <AvatarFallback className="bg-secondary text-[10px] font-semibold tracking-tight text-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-[13px] font-medium text-foreground',
              'group-data-[collapsible=icon]:hidden'
            )}
          >
            {triggerLabel}
          </span>
          <ChevronsUpDown
            aria-hidden
            className={cn(
              'ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground',
              'group-data-[collapsible=icon]:hidden'
            )}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" sideOffset={8} className="min-w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-foreground">{fullName}</span>
          <span className="text-xs font-normal text-muted-foreground">{me.email}</span>
        </DropdownMenuLabel>

        {me.memberships.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Organizations
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup value={activeOrg?.slug ?? ''} onValueChange={handleSwitch}>
              {me.memberships.map((m) => (
                <DropdownMenuRadioItem key={m.id} value={m.organization.slug}>
                  <span className="flex-1 truncate">{m.organization.name}</span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
